import React from 'react';
import { VisitStatusEnum } from '@api/READ_ONLY/visits_api/Api';
import AppAvatar from '@app/components/ui/Avatar';
import { ChipMultiSelect, type ChipOption } from '@app/components/ui/ChipSelect';
import { Panel } from '@app/features/dashboard/components';
import { visitEditPath } from '@app/features/dashboard/visitLinks';
import { getInitials } from '@app/utils/extractFullnameInitials';
import { Tag, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { FiChevronRight, FiEdit2 } from 'react-icons/fi';

import CardWindowNote from './CardWindowNote';
import { formatMonthHeading, formatRelativeDay, formatTime } from './clientCard.format';
import {
  CardVisit,
  CardWindow,
  CARD_VITALS,
  filterVisitsByStatus,
  groupVisitsByMonth,
  visitCaregivers,
  visitDurationMinutes,
  visitLateMinutes,
  visitServiceLines,
  visitStatusLabelKey,
  VISIT_STATUS_CHIP_STYLE,
  VISIT_STATUS_LABEL_KEYS,
  VISIT_STATUSES,
} from './clientCard.model';

/** antd tag colours for the four statuses. Completed is the only one worth colour. */
const STATUS_TAG_COLOR: Record<string, string | undefined> = {
  COMPLETED: 'green',
  IN_PROGRESS: 'blue',
  SCHEDULED: undefined,
  CANCELLED: undefined,
};

const VisitRow: React.FC<{ visit: CardVisit; onOpen: (visit: CardVisit) => void }> = ({ visit, onOpen }) => {
  const { t, i18n } = useTranslation();

  const relativeDayLabels = {
    today: t('client-card-today'),
    tomorrow: t('client-card-tomorrow'),
    yesterday: t('client-card-yesterday'),
  };

  const services = visitServiceLines(visit);
  const caregivers = visitCaregivers(visit);
  const late = visitLateMinutes(visit);

  // Only the vitals this visit actually recorded, in the catalogue's order so two
  // visits never list the same pair of readings in a different order.
  const measured = CARD_VITALS.filter((vital) => typeof visit[vital.key] === 'number');

  return (
    <div className="group flex items-start gap-2">
      {/* The body opens the drawer, which is the cheap look; the pencil beside it is the
          expensive one. Two controls and not one nested in the other, because a link
          inside a button is neither for the browser nor for a screen reader. */}
      <button
        type="button"
        onClick={() => onOpen(visit)}
        className="min-w-0 flex-1 cursor-pointer border-0 bg-transparent p-0 text-left"
      >
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="text-[13px] font-semibold text-slate-800" style={{ textWrap: 'pretty' }}>
            {services.map((service) => service.name).join(' · ') || t('client-card-visit-no-services')}
          </span>
          <Tag color={STATUS_TAG_COLOR[visit.status ?? '']} className="!mr-0">
            {t(visitStatusLabelKey(visit))}
          </Tag>
          {late !== null && (
            <span className="text-[11px] text-amber-600">{t('client-card-visit-late', { minutes: late })}</span>
          )}
        </div>

        {/* The caregiver carries a face, as on the scheduling columns: scanning a month
            for who came is what this line is read for, and an avatar is found faster
            than a name in a run of grey text. */}
        <div className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[12px] text-slate-500">
          <span>
            {`${formatRelativeDay(visit.startTime, i18n.language, relativeDayLabels)} ${formatTime(visit.startTime, i18n.language)}`}
          </span>
          <span className="text-slate-300">·</span>
          <span>{t('client-card-minutes', { minutes: visitDurationMinutes(visit) })}</span>
          <span className="text-slate-300">·</span>
          {caregivers.length === 0 ? (
            <span className="italic text-slate-400">{t('dashboard-note-unassigned-caregiver')}</span>
          ) : (
            caregivers.map((caregiver) => (
              <span key={caregiver.caregiverId} className="inline-flex items-center gap-1">
                <AppAvatar isSelected fullName={caregiver.name} value={getInitials(caregiver.name)} size={20} />
                <span className="text-slate-600">{caregiver.name}</span>
              </span>
            ))
          )}
        </div>

        {measured.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {measured.map((vital) => (
              <span
                key={vital.key}
                className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[11px] tabular-nums text-slate-600"
              >
                {t(vital.labelKey)} {visit[vital.key] as number}
                {vital.unit && <span className="text-slate-400"> {vital.unit}</span>}
              </span>
            ))}
          </div>
        )}
      </button>

      {/* A real anchor with `target`, as on the dashboard's notes feed: the reader is
          working down a history and losing the card to open one visit costs them their
          place. It also gets middle-click and the browser's own menu for free. */}
      <Tooltip title={t('dashboard-note-edit-visit')}>
        <a
          href={visitEditPath(visit.id)}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t('dashboard-note-edit-visit')}
          className="flex flex-none items-center rounded-md p-1.5 text-slate-300 transition-colors hover:bg-slate-100 hover:text-slate-700 group-hover:text-slate-400"
        >
          <FiEdit2 aria-hidden="true" className="text-sm" />
        </a>
      </Tooltip>
    </div>
  );
};

type CareTimelinePanelProps = {
  visits: CardVisit[];
  /** How many visits the fetched window really holds, so a truncated page says so. */
  total: number;
  window: CardWindow;
  onOpenVisit: (visit: CardVisit) => void;
  className?: string;
  bodyMaxHeight?: number;
};

/**
 * Every visit in the window, newest first, grouped under its month.
 *
 * Visits only. The notes and the files these visits produced have panels of their
 * own on this page, and repeating them here would make one scroll of history into
 * three copies of it.
 *
 * Months fold. A year of care is hundreds of rows, and the reader who wants last
 * March should not have to scroll through everything since — so only the newest
 * month is open to start with, and the rest state their visit count and wait.
 */
const CareTimelinePanel: React.FC<CareTimelinePanelProps> = ({
  visits,
  total,
  onOpenVisit,
  window,
  className,
  bodyMaxHeight = 520,
}) => {
  const { t, i18n } = useTranslation();

  // Empty means no filter. The reader who unticks their last status is putting the
  // control back, not asking for an empty page.
  const [statuses, setStatuses] = React.useState<VisitStatusEnum[]>([]);

  const shown = filterVisitsByStatus(visits, statuses);
  const groups = groupVisitsByMonth(shown);

  const statusOptions: ChipOption<VisitStatusEnum>[] = VISIT_STATUSES.map((status) => ({
    value: status,
    label: t(VISIT_STATUS_LABEL_KEYS[status]),
    color: VISIT_STATUS_CHIP_STYLE[status].fg,
    bg: VISIT_STATUS_CHIP_STYLE[status].bg,
    border: VISIT_STATUS_CHIP_STYLE[status].bd,
  }));

  // Only the months the reader has actually toggled. Everything else falls back to
  // the default below, so a month that arrives with a later page of visits opens
  // closed like its neighbours instead of needing an effect to catch it.
  const [toggled, setToggled] = React.useState<Record<string, boolean>>({});

  const isOpen = (month: string, index: number) => toggled[month] ?? index === 0;
  const allOpen = groups.every((group, index) => isOpen(group.month, index));

  const toggleMonth = (month: string, index: number) =>
    setToggled((prev) => ({ ...prev, [month]: !(prev[month] ?? index === 0) }));

  // One control rather than two: whichever state the months are not all in is the
  // one worth offering.
  const setAll = (open: boolean) => setToggled(Object.fromEntries(groups.map((group) => [group.month, open])));

  return (
    <Panel
      bodyHeight={bodyMaxHeight}
      title={t('client-card-timeline')}
      extra={
        <div className="flex items-center gap-3">
          <ChipMultiSelect<VisitStatusEnum>
            value={statuses}
            options={statusOptions}
            onChange={setStatuses}
            addLabel={t('client-card-timeline-status')}
            clearLabel={t('client-card-timeline-status-all')}
            summaryLabel={(count) => t('client-card-timeline-status-some', { count })}
            title={t('client-card-timeline-status')}
          />
          {groups.length > 1 && (
            <button
              type="button"
              onClick={() => setAll(!allOpen)}
              className="cursor-pointer border-0 bg-transparent p-0 text-[12px] text-sky-600 hover:text-sky-700 hover:underline"
            >
              {t(allOpen ? 'client-card-timeline-collapse-all' : 'client-card-timeline-expand-all')}
            </button>
          )}
          <CardWindowNote
            summary={
              // The truncation note is about the fetch, so it only stands while the
              // reader is looking at everything the fetch returned.
              statuses.length === 0 && total > visits.length
                ? t('client-card-timeline-count-truncated', { shown: visits.length, total })
                : t('client-card-timeline-count', { count: shown.length })
            }
            window={window}
          />
        </div>
      }
      className={className}
      pad="px-4 pb-4"
    >
      {groups.length === 0 ? (
        <div className="py-10 text-center text-[13px] text-slate-400">
          {t(statuses.length > 0 ? 'client-card-timeline-status-none' : 'client-card-timeline-empty')}
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto">
          {groups.map((group, index) => {
            const open = isOpen(group.month, index);
            const bodyId = `care-timeline-${group.month}`;

            return (
              <div key={group.month}>
                <button
                  type="button"
                  onClick={() => toggleMonth(group.month, index)}
                  aria-expanded={open}
                  aria-controls={bodyId}
                  className="sticky top-0 z-10 flex w-full cursor-pointer items-center gap-1.5 border-0 bg-white pb-2 pt-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-400 hover:text-slate-600"
                >
                  <FiChevronRight
                    aria-hidden="true"
                    className={`shrink-0 text-xs transition-transform ${open ? 'rotate-90' : ''}`}
                  />
                  <span>{formatMonthHeading(group.month, i18n.language)}</span>
                  {/* The count only while the rows are hidden: it is standing in for them. */}
                  {!open && (
                    <span className="ml-auto font-normal normal-case tracking-normal text-slate-400">
                      {t('client-card-timeline-count', { count: group.visits.length })}
                    </span>
                  )}
                </button>

                <div id={bodyId} hidden={!open}>
                  {group.visits.map((visit) => (
                    <div key={visit.id} className="flex gap-3">
                      <div className="flex shrink-0 flex-col items-center">
                        <span
                          className="mt-0.5 h-2.5 w-2.5 shrink-0 rounded-full"
                          style={{ background: visit.status === 'CANCELLED' ? '#cbd5e1' : 'rgb(2 132 199)' }}
                        />
                        <div className="my-1 w-px flex-1 bg-slate-200" />
                      </div>
                      <div className="min-w-0 flex-1 pb-4">
                        <VisitRow visit={visit} onOpen={onOpenVisit} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Panel>
  );
};

export default CareTimelinePanel;
